import { createClient, filter } from "@prismicio/client"
import { localStorage } from "@prismicio/editor-support/DOM"
import { useDebounce } from "@prismicio/editor-support/React"
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react"
import { z } from "zod"

import {
  type RequestRunReturnType,
  Requests,
  type RequestType,
  RequestTypes,
} from "./Request"
import {
  type Filter,
  FilterAnyArg,
  FilterTypes,
  queryFiltersToFilters,
} from "./Request/components/RequestFiltersFieldset"
import { queryOrderingsToOrderings } from "./Request/components/RequestOrderingsFieldset"
import { MissingParameterError } from "./Request/MissingParameterError"

const APIExplorerRequestProperties = z.object({
  repository: z
    .object({
      id: z.string().default(""),
      accessToken: z.string().optional(),
    })
    .default({}),
  type: z.enum(RequestTypes).default("get"),
  options: z
    .record(
      z.enum(RequestTypes),
      z
        .object({
          id: z.string().optional(),
          ids: z.array(z.string()).optional(),
          type: z.string().optional(),
          uid: z.string().optional(),
          uids: z.array(z.string()).optional(),
          tag: z.string().optional(),
          tags: z.array(z.string()).optional(),
        })
        .optional(),
    )
    .default({}),
  pagination: z
    .object({
      page: z.number().optional(),
      pageSize: z.number().optional(),
    })
    .default({}),
  lang: z.string().optional(),
  advanced: z.boolean().default(false),
  ref: z.string().optional(),
  filters: z
    .array(
      z.object({
        type: z.enum(FilterTypes),
        args: z.array(FilterAnyArg),
      }),
    )
    .default([])
    // Apply the more precise TypeScript type to the filters array.
    .transform((filters) => filters as Filter[]),
  orderings: z
    .array(
      z.object({
        field: z.string(),
        direction: z.enum(["asc", "desc"]).optional(),
      }),
    )
    .default([]),
})

export type APIExplorerRequestProperties = z.infer<
  typeof APIExplorerRequestProperties
>

type APIExplorerRequestMethods = {
  setRepositoryID: (id: string) => void
  setRepositoryAccessToken: (token?: string) => void
  setRequestType: (type: RequestType) => void
  setRequestOptions: <Type extends RequestType>(
    type: Type,
    options: APIExplorerRequestProperties["options"][Type],
  ) => void
  setPagination: (pagination: { page?: number; pageSize?: number }) => void
  setLang: (lang?: string) => void
  setAdvanced: (advanced: boolean) => void
  setRef: (ref?: string) => void
  setFilters: (filters: APIExplorerRequestProperties["filters"]) => void
  setOrderings: (orderings: APIExplorerRequestProperties["orderings"]) => void
  run: () => Promise<void>
}

type ResponseBase = { duration: number }
type ResponseIdle = {
  status: "idle"
}
type ResponsePending = {
  status: "pending"
}
export type ResponseSuccess = ResponseBase & {
  status: "success"
  url?: string
  ref?: string
  data: RequestRunReturnType
}
export type ResponseError = ResponseBase & {
  status: "error"
  url?: string
  error: Error
}

type APIExplorerResponse =
  | ResponseIdle
  | ResponsePending
  | ResponseSuccess
  | ResponseError

type APIExplorerConfig = {
  /**
   * Repository information to use the API Explorer with.
   * When not provided, the API Explorer will allow entering a repository ID.
   */
  repository?: {
    id: string
    documentAPIEndpoint?: string
  }
}

type APIExplorerContextType = {
  request: APIExplorerRequestProperties & APIExplorerRequestMethods
  response: APIExplorerResponse
  config: APIExplorerConfig
}

const APIExplorerContext = createContext<APIExplorerContextType | undefined>(
  undefined,
)

export type APIExplorerEvents =
  | [
      event: "API Explorer Request",
      args: {
        type: RequestType
        repositoryID: string
      },
    ]
  | [
      event: "API Explorer Request Error",
      args: {
        type: RequestType
        repositoryID: string
        error?: string
        url?: string
      },
    ]

export type APIExplorerProviderProps = {
  track?: (...params: APIExplorerEvents) => void
} & APIExplorerConfig &
  PropsWithChildren

// Holds the abort controller for the last request to allow
// aborting it when a new request is made.
let runAbortController: AbortController | undefined

export const APIExplorerProvider = (props: APIExplorerProviderProps) => {
  const { track = () => {}, children, ...config } = props

  const [requestProperties, setRequestProperties] = useState(
    getInitialRequestProperties(config.repository),
  )

  const requestMethods: APIExplorerRequestMethods = {
    setRepositoryID(id: string) {
      setRequestProperties((prev) => ({
        ...prev,
        repository: {
          ...prev.repository,
          id,
        },
      }))
    },
    setRepositoryAccessToken(token?: string) {
      setRequestProperties((prev) => ({
        ...prev,
        repository: {
          ...prev.repository,
          accessToken: token || undefined,
        },
      }))
    },
    setRequestType(type: RequestType) {
      setRequestProperties((prev) => ({
        ...prev,
        type,
      }))
    },
    setRequestOptions(type, options) {
      setRequestProperties((prev) => ({
        ...prev,
        options: {
          ...prev.options,
          [type]: options,
        },
      }))
    },
    setPagination(pagination) {
      setRequestProperties((prev) => ({
        ...prev,
        pagination,
      }))
    },
    setLang(lang) {
      setRequestProperties((prev) => ({
        ...prev,
        lang: lang || undefined,
      }))
    },
    setRef(ref) {
      setRequestProperties((prev) => ({
        ...prev,
        ref: ref || undefined,
      }))
    },
    setFilters(filters) {
      setRequestProperties((prev) => ({
        ...prev,
        filters,
      }))
    },
    setOrderings(orderings) {
      setRequestProperties((prev) => ({
        ...prev,
        orderings,
      }))
    },
    setAdvanced(advanced) {
      setRequestProperties((prev) => ({
        ...prev,
        advanced,
      }))
    },
    async run() {
      if (runAbortController) {
        runAbortController.abort()
      }
      runAbortController = new AbortController()

      if (!requestProperties.repository.id) {
        setResponse({ status: "idle" })
        return
      }

      setResponse({ status: "pending" })
      track("API Explorer Request", {
        type: request.type,
        repositoryID: request.repository.id,
      })

      let ref: string | undefined
      let url: string | undefined

      const client = createClient(requestProperties.repository.id, {
        documentAPIEndpoint: config.repository?.documentAPIEndpoint,
        accessToken: requestProperties.repository.accessToken,
        fetch(input, init) {
          const urlObject = new URL(input)

          // Replace the x-c tracking parameter to differentiate API Explorer requests
          const xC = urlObject.searchParams.get("x-c")
          if (xC) {
            urlObject.searchParams.set(
              "x-c",
              xC.replace("js-", "api-explorer-"),
            )
          }

          // Keep track of the last used ref and URL
          ref = urlObject.searchParams.get("ref") ?? undefined
          url = urlObject.toString()

          return fetch(url, init)
        },
      })

      const start = Date.now()

      try {
        const maybeAdvancedOptions = request.advanced
          ? {
              ref: request.ref,
              filters: requestProperties.filters.map(({ type, args }) => {
                // Cast filter function type to allow loosely typed args
                const filterFn = filter[type] as (...args: unknown[]) => string
                return filterFn(...args)
              }),
              orderings: request.orderings,
            }
          : {}

        const data = await Requests[requestProperties.type].run(client, {
          ...requestProperties.options[requestProperties.type],
          ...requestProperties.pagination,
          lang: request.lang,
          ...maybeAdvancedOptions,
          signal: runAbortController.signal,
        })
        setResponse({
          status: "success",
          duration: Date.now() - start,
          ref,
          url,
          data,
        })
      } catch (rawError) {
        const error =
          rawError instanceof Error ? rawError : new Error("Unknown error")

        if (error.name === "AbortError") {
          return
        }

        setResponse({
          status: "error",
          duration: Date.now() - start,
          url,
          error,
        })
        track("API Explorer Request Error", {
          type: request.type,
          repositoryID: request.repository.id,
          url,
          error:
            error instanceof MissingParameterError
              ? "MissingParameterError"
              : error.message,
        })
      }
    },
  }

  const request = {
    ...requestProperties,
    ...requestMethods,
  }

  const [response, setResponse] = useState<APIExplorerResponse>({
    status: "idle",
  })

  useEffect(() => {
    void request.run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useDebounce(requestProperties, 200, request.run)

  // Preserve state in local storage
  useEffect(() => {
    localStorage.write(
      apiExplorerLocalStorageKey,
      JSON.stringify(requestProperties),
    )
  }, [requestProperties])

  return (
    <APIExplorerContext.Provider value={{ request, response, config }}>
      {children}
    </APIExplorerContext.Provider>
  )
}

export function useAPIExplorer(): APIExplorerContextType {
  const context = useContext(APIExplorerContext)

  if (!context) {
    throw new Error("useAPIExplorer must be used within an APIExplorerProvider")
  }

  return context
}

export const apiExplorerShareableUrlSearchParam = "request"

const apiExplorerLocalStorageKey = "prismic-api-explorer"
const unsupportedDocumentAPISearchParams = [
  "after",
  "graphQuery",
  "fetch",
  "fetchLinks",
  "integrationFieldsRef",
  "routes",
  "brokenRoute",
]

function getInitialRequestProperties(
  repository: APIExplorerProviderProps["repository"],
): APIExplorerRequestProperties {
  // Retrieve request from shareable URL if available first
  const url = new URL(window.location.href)
  if (url.searchParams.has(apiExplorerShareableUrlSearchParam)) {
    try {
      const parsedRequest: unknown = JSON.parse(
        url.searchParams.get(apiExplorerShareableUrlSearchParam) ?? "",
      )
      const validatedRequest =
        APIExplorerRequestProperties.safeParse(parsedRequest)

      if (validatedRequest.success) {
        // Preserve repository ID from configuration if set.
        if (repository?.id) {
          validatedRequest.data.repository.id = repository.id
        }
        // Remove state from URL so that refreshing the page uses the local storage state
        // instead of reapplying the URL state.
        url.searchParams.delete(apiExplorerShareableUrlSearchParam)
        window.history.replaceState({}, "", url.toString())

        // Update local storage with the new state
        localStorage.write(
          apiExplorerLocalStorageKey,
          JSON.stringify(validatedRequest.data),
        )

        return validatedRequest.data
      } else {
        console.warn(
          "Could not retrieve API Explorer request from URL",
          validatedRequest.error,
        )
      }
    } catch (error) {
      console.warn("Could not parse API Explorer request from URL", error)
    }
  }

  // Resolve request from an API response if available (only on private explorers).
  if (repository?.id && url.searchParams.has("ref")) {
    const unsupportedParams: string[] = []
    for (const param of unsupportedDocumentAPISearchParams) {
      if (url.searchParams.has(param)) {
        unsupportedParams.push(param)
      }
    }

    if (unsupportedParams.length > 0) {
      window.alert(
        `The following search parameters from the Document API are not yet supported in the API Explorer and will be ignored: ${unsupportedParams.join(", ")}`,
      )
    }

    const request = optimizeFiltersToClientMethods({
      repository: {
        id: repository.id,
        accessToken: url.searchParams.get("accessToken") ?? undefined,
      },
      type: "get",
      options: {},
      pagination: {
        page: url.searchParams.has("page")
          ? Number(url.searchParams.get("page"))
          : undefined,
        pageSize: url.searchParams.has("pageSize")
          ? Number(url.searchParams.get("pageSize"))
          : undefined,
      },
      lang: url.searchParams.get("lang") ?? undefined,
      // When redirecting from a .cdn URL, `ref` is always set, so we can assume advanced mode.
      advanced: true,
      ref: url.searchParams.get("ref") ?? undefined,
      filters: queryFiltersToFilters(url.searchParams.getAll("q")),
      orderings: queryOrderingsToOrderings(
        url.searchParams.getAll("orderings"),
      ),
    })

    // Remove query params from URL so that refreshing the page uses the local storage request
    // instead of reapplying the URL query params.
    window.history.replaceState({}, "", url.toString().split("?")[0])

    // Update local storage with the new request
    localStorage.write(apiExplorerLocalStorageKey, JSON.stringify(request))

    return request
  }

  // Retrieve previous request from local storage if available.
  if (localStorage.has(apiExplorerLocalStorageKey)) {
    const storedRequest = localStorage.readJSON(
      apiExplorerLocalStorageKey,
      APIExplorerRequestProperties,
    )

    if (storedRequest) {
      return storedRequest
    } else {
      console.warn("Could not retrieve API Explorer request from local storage")
    }
  }

  // Fallback to a default request if no request is available.
  return {
    repository: {
      id: repository?.id ?? "",
    },
    type: "get",
    options: {},
    pagination: {},
    advanced: false,
    filters: [],
    orderings: [],
  }
}

function optimizeFiltersToClientMethods(
  request: APIExplorerRequestProperties,
): APIExplorerRequestProperties {
  const maybeGetByTypeFilter = request.filters.findIndex(
    (filter) => filter.type === "at" && filter.args[0] === "document.type",
  )
  if (maybeGetByTypeFilter >= 0) {
    const maybeType = request.filters[maybeGetByTypeFilter]?.args[1]

    if (typeof maybeType === "string") {
      const maybeGetByUIDFilter = request.filters.findIndex(
        (filter) =>
          filter.type === "at" && filter.args[0] === `my.${maybeType}.uid`,
      )

      if (maybeGetByUIDFilter >= 0) {
        const maybeUID = request.filters[maybeGetByUIDFilter]?.args[1]

        if (typeof maybeUID === "string") {
          request.pagination.pageSize = undefined
          request.type = "getByUID"
          request.options.getByUID = {
            type: request.filters[maybeGetByTypeFilter]?.args[1] as string,
            uid: request.filters[maybeGetByUIDFilter]?.args[1] as string,
          }

          // Remove "optimized" filters
          request.filters = request.filters.filter(
            (_, index) => index !== maybeGetByUIDFilter,
          )
          request.filters = request.filters.filter(
            (_, index) => index !== maybeGetByTypeFilter,
          )
        }
      } else {
        if (request.pagination.pageSize === 1 && !request.pagination.page) {
          request.pagination.pageSize = undefined
          request.type = "getSingle"
          request.options.getSingle = {
            type: request.filters[maybeGetByTypeFilter]?.args[1] as string,
          }
        } else {
          request.type = "getByType"
          request.options.getByType = {
            type: request.filters[maybeGetByTypeFilter]?.args[1] as string,
          }
        }

        // Remove "optimized" filters
        request.filters = request.filters.filter(
          (_, index) => index !== maybeGetByTypeFilter,
        )
      }
    }
  }

  return request
}
